import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { getDisplayName } from '../utils/displayHelpers';
import { 
  Calendar,
  Clock,
  CreditCard,
  CheckCircle,
  ArrowLeft,
  MapPin,
  IndianRupee,
  Shield,
  AlertTriangle,
  ChevronRight,
  Star,
  MessageCircle,
  Heart
} from 'lucide-react';
import DataFetchState from '../components/ui/DataFetchState';
import AnimatedEmptyState from '../components/ui/AnimatedEmptyState';

const BookingFlow = () => {
  const { caregiverId } = useParams();
  const navigate = useNavigate();
  const { caregivers, caregiversLoading, caregiversError, fetchCaregivers, pets, createBooking } = useData();
  const { user } = useAuth();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [bookingData, setBookingData] = useState({
    caregiverId,
    petId: '',
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    pickupType: 'dropoff',
    notes: '',
    duration: 'hourly'
  });

  const caregiver = caregivers.find(c => c.id === caregiverId);
  const userPets = pets.filter(pet => pet.ownerId === user?.id);
  const totalSteps = 4;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  if (caregiversLoading || caregiversError) {
    return (
      <div className="pf-page-wrap pb-12">
        <div className="max-w-2xl mx-auto px-4 py-16">
          <DataFetchState
            loading={caregiversLoading}
            error={caregiversError}
            onRetry={fetchCaregivers}
            loadingLabel="Loading caregiver profile"
            errorTitle="Couldn't load booking details"
          />
        </div>
      </div>
    );
  }

  if (!caregiver) {
    return (
      <div className="pf-page-wrap pb-12">
        <div className="max-w-2xl mx-auto px-4 py-16">
          <AnimatedEmptyState
            icon="loader-running-dog"
            title="Caregiver not found"
            description="This profile may have been removed or the link is incorrect."
            action={
              <Link to="/search" className="pf-btn-primary">
                Browse caregivers
              </Link>
            }
          />
        </div>
      </div>
    );
  }

  const calculatePrice = () => {
    if (!bookingData.startDate || !bookingData.endDate) return 0;
    
    const start = new Date(`${bookingData.startDate}T${bookingData.startTime}`);
    const end = new Date(`${bookingData.endDate}T${bookingData.endTime}`);
    const hours = Math.ceil((end - start) / (1000 * 60 * 60));
    
    switch (bookingData.duration) {
      case 'hourly':
        return hours * caregiver.hourlyRate;
      case 'daily':
        const days = Math.ceil(hours / 24);
        return days * caregiver.dailyRate;
      case 'monthly':
        return caregiver.monthlyRate;
      default:
        return hours * caregiver.hourlyRate;
    }
  };

  const handleSubmit = async () => {
    const price = calculatePrice();
    const booking = {
      ...bookingData,
      ownerId: user.id,
      petIds: [bookingData.petId],
      start: `${bookingData.startDate}T${bookingData.startTime}:00Z`,
      end: `${bookingData.endDate}T${bookingData.endTime}:00Z`,
      escrowAmount: price,
      commissionPercent: 15,
      status: 'requested'
    };

    createBooking(booking);
    setCurrentStep(4);
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-1">
        {Array.from({ length: 5 }, (_, i) => (
          <Star
            key={i}
            className={`w-4 h-4 ${
              i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  const renderStep1 = () => (
    <div className="space-y-8">
      <div className="flex items-center gap-6 pb-6 border-b border-gray-200">
        <img
          src={caregiver.user.avatar}
          alt={getDisplayName(caregiver.user)}
          className="w-20 h-20 rounded-xl object-cover"
        />
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-1">{getDisplayName(caregiver.user)}</h2>
          <p className="text-gray-600 mb-2">{caregiver.title}</p>
          <div className="flex items-center gap-3">
            {renderStars(caregiver.rating)}
            <span className="text-gray-600">({caregiver.reviewCount} reviews)</span>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Your Pet</h3>
        <div className="grid gap-4">
          {userPets.map((pet) => (
            <label key={pet.id} className="relative">
              <input
                type="radio"
                name="petId"
                value={pet.id}
                checked={bookingData.petId === pet.id}
                onChange={(e) => setBookingData({...bookingData, petId: e.target.value})}
                className="sr-only"
              />
              <div className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                bookingData.petId === pet.id
                  ? 'border-[var(--pf-accent)] bg-[var(--pf-accent-soft)]'
                  : 'border-gray-200 hover:border-[color-mix(in_srgb,var(--pf-accent)_40%,var(--pf-border))]'
              }`}>
                <div className="flex items-center gap-4">
                  <img
                    src={pet.photos[0]}
                    alt={pet.name}
                    className="w-16 h-16 rounded-lg object-cover"
                  />
                  <div>
                    <h4 className="font-medium text-gray-900">{pet.name}</h4>
                    <p className="text-sm text-gray-600">{pet.breed} • {pet.age} years old</p>
                  </div>
                  {bookingData.petId === pet.id && (
                    <CheckCircle className="w-5 h-5 text-[var(--pf-accent)] ml-auto" />
                  )}
                </div>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Booking Type</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { type: 'hourly', label: 'Hourly Care', rate: caregiver.hourlyRate, icon: Clock },
            { type: 'daily', label: 'Daily Care', rate: caregiver.dailyRate, icon: Calendar },
            { type: 'monthly', label: 'Monthly Care', rate: caregiver.monthlyRate, icon: Calendar }
          ].map(({ type, label, rate, icon: Icon }) => (
            <label key={type} className="relative">
              <input
                type="radio"
                name="duration"
                value={type}
                checked={bookingData.duration === type}
                onChange={(e) => setBookingData({...bookingData, duration: e.target.value})}
                className="sr-only"
              />
              <div className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                bookingData.duration === type
                  ? 'border-[var(--pf-accent)] bg-[var(--pf-accent-soft)]'
                  : 'border-gray-200 hover:border-[color-mix(in_srgb,var(--pf-accent)_40%,var(--pf-border))]'
              }`}>
                <div className="flex flex-col items-center text-center gap-2">
                  <Icon className={`w-6 h-6 ${
                    bookingData.duration === type ? 'text-[var(--pf-accent)]' : 'text-gray-400'
                  }`} />
                  <div>
                    <p className="font-medium text-gray-900">{label}</p>
                    <p className="text-sm text-gray-600">₹{rate}/{type === 'hourly' ? 'hr' : type === 'daily' ? 'day' : 'month'}</p>
                  </div>
                </div>
              </div>
            </label>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Dates & Times</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <input
              type="date"
              value={bookingData.startDate}
              onChange={(e) => setBookingData({...bookingData, startDate: e.target.value})}
              className="form-input"
              min={new Date().toISOString().split('T')[0]}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="date"
              value={bookingData.endDate}
              onChange={(e) => setBookingData({...bookingData, endDate: e.target.value})}
              className="form-input"
              min={bookingData.startDate || new Date().toISOString().split('T')[0]}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Time
            </label>
            <input
              type="time"
              value={bookingData.startTime}
              onChange={(e) => setBookingData({...bookingData, startTime: e.target.value})}
              className="form-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Time
            </label>
            <input
              type="time"
              value={bookingData.endTime}
              onChange={(e) => setBookingData({...bookingData, endTime: e.target.value})}
              className="form-input"
            />
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Pickup/Dropoff Preference</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { type: 'dropoff', label: 'Drop Off', description: 'Drop your pet at caregiver\'s home' },
            { type: 'pickup', label: 'Pick Up', description: 'Caregiver picks up from your home' },
            { type: 'onsite', label: 'On-site Care', description: 'Care at your home' }
          ].map(({ type, label, description }) => (
            <label key={type} className="relative">
              <input
                type="radio"
                name="pickupType"
                value={type}
                checked={bookingData.pickupType === type}
                onChange={(e) => setBookingData({...bookingData, pickupType: e.target.value})}
                className="sr-only"
              />
              <div className={`p-4 border-2 rounded-xl cursor-pointer transition-all h-full ${
                bookingData.pickupType === type
                  ? 'border-[var(--pf-accent)] bg-[var(--pf-accent-soft)]'
                  : 'border-gray-200 hover:border-[color-mix(in_srgb,var(--pf-accent)_40%,var(--pf-border))]'
              }`}>
                <div className="text-center">
                  <p className="font-medium text-gray-900 mb-1">{label}</p>
                  <p className="text-sm text-gray-600">{description}</p>
                </div>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Special Instructions</h3>
        <textarea
          value={bookingData.notes}
          onChange={(e) => setBookingData({...bookingData, notes: e.target.value})}
          className="form-input h-32 resize-none"
          placeholder="Any special care instructions, medication schedules, or preferences..."
        />
      </div>
    </div>
  );

  const renderStep3 = () => {
    const price = calculatePrice();
    const platformFee = Math.round(price * 0.15);
    const total = price + platformFee;

    return (
      <div className="space-y-8">
        <div className="pf-organic-card bg-[var(--pf-accent-soft)] p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Booking Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-gray-600 mb-1">Caregiver</p>
              <div className="flex items-center gap-3">
                <img
                  src={caregiver.user.avatar}
                  alt={getDisplayName(caregiver.user)}
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                  <p className="font-medium text-gray-900">{getDisplayName(caregiver.user)}</p>
                  <div className="flex items-center gap-1">
                    {renderStars(caregiver.rating)}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <p className="text-sm text-gray-600 mb-1">Pet</p>
              <div className="flex items-center gap-3">
                <img
                  src={userPets.find(p => p.id === bookingData.petId)?.photos[0]}
                  alt={userPets.find(p => p.id === bookingData.petId)?.name}
                  className="w-10 h-10 rounded-lg object-cover"
                />
                <div>
                  <p className="font-medium text-gray-900">
                    {userPets.find(p => p.id === bookingData.petId)?.name}
                  </p>
                  <p className="text-sm text-gray-600">
                    {userPets.find(p => p.id === bookingData.petId)?.breed}
                  </p>
                </div>
              </div>
            </div>

            <div>
              <p className="text-sm text-gray-600 mb-1">Dates</p>
              <div className="flex items-center gap-2 text-gray-900">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span>{bookingData.startDate} to {bookingData.endDate}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600 mt-1">
                <Clock className="w-4 h-4 text-gray-400" />
                <span>{bookingData.startTime} - {bookingData.endTime}</span>
              </div>
            </div>

            <div>
              <p className="text-sm text-gray-600 mb-1">Service Type</p>
              <div className="flex items-center gap-2">
                <p className="font-medium text-gray-900 capitalize">{bookingData.duration} Care</p>
                <span className="text-gray-400">•</span>
                <p className="text-gray-600 capitalize">{bookingData.pickupType}</p>
              </div>
            </div>
          </div>

          {bookingData.notes && (
            <div className="mt-6 pt-6 border-t border-[var(--pf-border)]">
              <p className="text-sm text-gray-600 mb-2">Special Instructions</p>
              <p className="text-gray-900">{bookingData.notes}</p>
            </div>
          )}
        </div>

        <div className="card bg-white">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Details</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Service Fee</span>
              <span className="font-medium">₹{price}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Platform Fee (15%)</span>
              <span className="font-medium">₹{platformFee}</span>
            </div>
            <div className="flex justify-between pt-3 border-t border-gray-200">
              <span className="font-semibold text-gray-900">Total</span>
              <span className="font-semibold text-gray-900">₹{total}</span>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-100 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <Shield className="w-6 h-6 text-blue-600 mt-1" />
            <div>
              <h4 className="font-medium text-gray-900 mb-1">Protected Payments</h4>
              <p className="text-sm text-gray-600">
                Your payment will be held securely in escrow until the service is completed.
                Our platform ensures safe transactions and provides buyer protection.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="pf-page-wrap pb-32 lg:pb-20">
      <div className="container max-w-4xl py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-[var(--pf-accent)] mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Book Pet Care Service</h1>
          <p className="text-gray-600">Complete your booking in a few simple steps</p>
        </div>

        {/* Progress Steps */}
        <div className="card mb-8">
          <div className="flex items-center justify-between">
            {[
              { step: 1, title: 'Select Pet & Service' },
              { step: 2, title: 'Add Details' },
              { step: 3, title: 'Review & Pay' },
              { step: 4, title: 'Confirmation' }
            ].map((step, index) => (
              <div key={step.step} className="flex items-center">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                  currentStep > step.step
                    ? 'bg-green-500 border-green-500 text-white'
                    : currentStep === step.step
                      ? 'bg-[var(--pf-accent)] border-[var(--pf-accent)] text-white'
                      : 'border-gray-300 text-gray-400'
                }`}>
                  {currentStep > step.step ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <span className="text-sm font-medium">{step.step}</span>
                  )}
                </div>
                <div className="ml-3">
                  <p className={`text-sm font-medium ${
                    currentStep === step.step ? 'text-[var(--pf-accent)]' : 'text-gray-500'
                  }`}>
                    {step.title}
                  </p>
                </div>
                {index < 3 && (
                  <div className={`w-full h-0.5 mx-4 ${
                    currentStep > step.step ? 'bg-green-500' : 'bg-gray-300'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="card">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Booking Confirmed!</h2>
              <p className="text-gray-600 mb-8">
                Your booking request has been sent to {getDisplayName(caregiver.user)}. 
                You'll receive a notification once they respond.
              </p>
              <div className="flex gap-4 justify-center">
                <Link
                  to="/owner-bookings"
                  className="btn-primary"
                >
                  View Bookings
                </Link>
                <Link
                  to="/owner-dashboard"
                  className="btn-outline"
                >
                  Go to Dashboard
                </Link>
              </div>
            </div>
          )}

          {/* Navigation */}
          {currentStep < 4 && (
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
              <button
                onClick={() => setCurrentStep(currentStep - 1)}
                disabled={currentStep === 1}
                className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                  currentStep === 1
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                Back
              </button>

              <button
                onClick={() => {
                  if (currentStep === 3) {
                    handleSubmit();
                  } else {
                    setCurrentStep(currentStep + 1);
                  }
                }}
                disabled={
                  (currentStep === 1 && !bookingData.petId) ||
                  (currentStep === 2 && (!bookingData.startDate || !bookingData.endDate))
                }
                className="btn-primary px-8 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {currentStep === 3 ? 'Confirm & Pay' : 'Continue'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingFlow;